// src/components/reports/meetingRoadmap.js
//
// The Recommendations & Roadmap section of the Meeting Notes: what gets frozen
// into a meeting, and the one field that deliberately does not.
//
// React-free so the rule can be unit-tested on its own — it is the part of this
// feature most likely to be got subtly wrong.

/* ── FROZEN vs LIVE — the whole rule, in one place ───────────────────────────
 *
 * A meeting's section is a HISTORICAL RECORD of what was discussed, so the item
 * SET and every descriptive field are frozen when the notes are generated:
 *   • an item added to the roadmap afterwards does NOT appear in that meeting
 *   • an item later deleted from the roadmap STILL appears — it was discussed
 *
 * ⛔ QUOTE-STATE IS THE ONE EXCEPTION, and freezing it would break the feature
 * outright: the Create Quote button mutates the LIVE roadmap item, so a frozen
 * badge would still read "Not quoted" after a successful click and the button
 * would appear to do nothing. Quote-state is therefore looked up live, by stable
 * id, at view time — never copied into the snapshot.
 */
const FROZEN_FIELDS = [
  "id", "title", "detail", "cost", "category", "targetMonth",
  "status", "budgetFy", "kind", "source",
];

/**
 * Freeze the client's roadmap into the shape stored on the meeting's brief.
 *
 * ⚠ Takes the WHOLE `/api/roadmap` payload (items + recs) and flattens it, so
 * the meeting does not have to know that budget lines and recommendations live
 * in two arrays. `companyId` is carried so a later render can prove the section
 * belongs to the meeting's client rather than trusting its position on the page.
 */
export function freezeRoadmapForMeeting(roadmap) {
  if (!roadmap) return null;
  const take = (row, defaults) => {
    const out = { ...defaults };
    FROZEN_FIELDS.forEach((f) => { if (row[f] !== undefined) out[f] = row[f]; });
    return out;
  };
  const items = (roadmap.items || []).map((i) => take(i, { kind: null, source: "budget" }));
  const recs = (roadmap.recs || [])
    /* ⛔ APPROVED ONLY. The same rule the client report applies — a draft has not
       been through Craig, and this page is shown to the client in the meeting. */
    .filter((r) => r && r.approved === true)
    .map((r) => take(r, { status: "recommendation", category: r.kind || "recommendation" }));

  return {
    companyId: String(roadmap.companyId || ""),
    fy: roadmap.fy || null,
    frozenAt: new Date().toISOString(),
    items: [...items, ...recs],
  };
}

/**
 * Re-attach LIVE quote-state to a frozen snapshot, matching on stable id.
 *
 * ⛔ PER-CLIENT ISOLATION IS ENFORCED HERE, not assumed. If the live roadmap is
 * for a different company than the snapshot, nothing is merged — the ids are
 * opaque strings and a budget line id could in principle repeat across clients,
 * which is exactly the Hedland/Rottnest class of mistake. Better a section with
 * no badges than one showing another client's quoting.
 *
 * An item the live roadmap no longer has keeps its frozen row and simply shows
 * no quote-state — it was discussed, and that is what the meeting records.
 */
export function mergeLiveQuoteState(snapshot, liveRoadmap) {
  if (!snapshot) return null;
  const sameClient = liveRoadmap
    && String(liveRoadmap.companyId || "") === String(snapshot.companyId || "")
    && String(snapshot.companyId || "") !== "";

  const live = new Map();
  if (sameClient) {
    [...(liveRoadmap.items || []), ...(liveRoadmap.recs || [])]
      .forEach((r) => { if (r && r.id != null) live.set(String(r.id), r); });
  }

  return {
    ...snapshot,
    isolationOk: !liveRoadmap || sameClient,
    items: (snapshot.items || []).map((frozen) => {
      const l = live.get(String(frozen.id));
      return {
        ...frozen,
        // ⚠ LIVE, never from the snapshot — see the rule at the top of this file.
        quoteState: l?.quoteState || "not_quoted",
        linkedQuoteId: l?.linkedQuoteId || null,
        quotedAt: l?.quotedAt || null,
        quoteNumber: l?.quoteNumber || null,
        stillOnRoadmap: !!l,
      };
    }),
  };
}

/* Grouping, deliberately mirroring the monthly report's section so the two read
   consistently — a client seeing both should not have to reconcile them. */
export function groupForMeeting(items) {
  const rows = items || [];
  const isRec = (i) => i.status === "recommendation";
  return {
    scheduled: rows.filter((i) => i.status === "project")
      .sort((a, b) => String(a.targetMonth || "9999-99").localeCompare(String(b.targetMonth || "9999-99"))),
    budgeted: rows.filter((i) => !isRec(i) && i.status !== "project"),
    quickWins: rows.filter((i) => isRec(i) && (i.kind === "quick-win" || i.category === "quick-win")),
    recommended: rows.filter((i) => isRec(i) && i.kind !== "quick-win" && i.category !== "quick-win"),
  };
}

/** "Budgeted · Quoted" — the two axes, side by side and never collapsed. */
export function statusLabel(item) {
  if (item?.status === "project") return "Scheduled project";
  if (item?.status === "recommendation") {
    return (item.kind === "quick-win" || item.category === "quick-win") ? "Quick win" : "Recommendation";
  }
  return "Budgeted";
}

export { FROZEN_FIELDS };
